import {
  FC,
  FocusEvent,
  FocusEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef
} from 'react';

type FocusGroupProps = {
  className?: string;
  children: ReactNode;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
};

export const FocusGroup: FC<FocusGroupProps> = ({
  children,
  className,
  onBlur,
  onFocus
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastFocusEventRef = useRef<FocusEvent | null>();
  const onBlurRef = useRef<typeof onBlur>();
  onBlurRef.current = onBlur;

  useEffect(() => {
    return () => {
      if (lastFocusEventRef.current) {
        onBlurRef.current?.({
          ...lastFocusEventRef.current,
          type: 'blur'
        });
      }
    };
  }, []);

  const eventOutOfContainer = useCallback((event: FocusEvent): boolean => {
    const container = containerRef.current;
    // Event started/ended outside if relatedTarget is `null` or not within the container.
    return (
      !!container
      && (
        !event.relatedTarget
        || !container.contains(event.relatedTarget as Node)
      )
    );
  }, []);

  const handleFocus = useCallback<FocusEventHandler<HTMLDivElement>>((event) => {
    // Focus entered from outside if relatedTarget is null or not within the container.
    if (onFocus && eventOutOfContainer(event)) {
      lastFocusEventRef.current = event;
      onFocus(event);
    }
  }, [eventOutOfContainer, onFocus]);

  const handleBlur = useCallback<FocusEventHandler<HTMLDivElement>>((event) => {
    // Focus left entirely if relatedTarget is null or not within the container.
    if (onBlur && eventOutOfContainer(event)) {
      lastFocusEventRef.current = null;
      onBlur(event);
    }
  }, [eventOutOfContainer, onBlur]);

  return (
    <div className={className} onBlur={handleBlur} onFocus={handleFocus} ref={containerRef}>
      {children}
    </div>
  );
};
